/* ============================================================
 * ANWEBSITE — Reveal Animation Controller
 * File: assets/anweb-reveal.js
 *
 * - IntersectionObserver: trigger reveal khi element vào viewport
 * - MutationObserver: tự bắt elements load qua AJAX
 *   (quan trọng cho filter/sort/pagination — site có vài trăm sản phẩm)
 * - Auto stagger cho children
 *
 * Markup API:
 *   <section data-reveal="fade-up">...</section>
 *   <div data-reveal="zoom-in" data-reveal-delay="200">...</div>
 *
 *   Stagger mọi children trực tiếp:
 *   <div data-reveal-stagger="fade-up" data-reveal-stagger-delay="80">
 *     <div>Child 1</div>
 *     <div>Child 2</div>
 *   </div>
 *
 *   Chỉ stagger child cụ thể:
 *   <div data-reveal-stagger>
 *     <div data-reveal-child>Item</div>
 *     <p>Not animated</p>
 *     <div data-reveal-child>Item</div>
 *   </div>
 *
 * Attributes tuỳ chọn:
 *   data-reveal-delay="200"           — ms, delay riêng cho element này
 *   data-reveal-duration="1000"       — ms, override duration
 *   data-reveal-stagger-delay="100"   — ms, gap giữa các child khi stagger
 *   data-reveal-threshold="0.2"       — 0..1, % element visible trước khi trigger
 *   data-reveal-once="false"          — nếu "false", trigger lại khi scroll ra/vào
 * ============================================================ */

(function () {
  'use strict';

  // Đánh dấu sẵn sàng — CSS dùng class này để hide initial state.
  // Phải set sớm nhất có thể để tránh FOUC.
  document.documentElement.classList.add('anweb-reveal-ready');

  // Bỏ qua nếu user prefer reduced motion — CSS đã handle, JS không cần làm gì
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document
      .querySelectorAll('[data-reveal], [data-reveal-child], [data-reveal-stagger] > *')
      .forEach(function (el) {
        el.classList.add('is-revealed');
      });
    return;
  }

  // Bỏ qua trong Shopify editor (merchant đang customize) — CSS đã handle
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }

  // Browser quá cũ không có IntersectionObserver → reveal tất cả ngay
  if (!('IntersectionObserver' in window)) {
    document
      .querySelectorAll('[data-reveal], [data-reveal-child], [data-reveal-stagger]')
      .forEach(function (el) {
        el.classList.add('is-revealed');
      });
    return;
  }

  // ============================================================
  // CONFIG
  // ============================================================
  var DEFAULT_THRESHOLD = 0.12;
  var DEFAULT_ROOT_MARGIN = '0px 0px -8% 0px'; // trigger sớm hơn 8% từ dưới
  var DEFAULT_STAGGER_GAP = 80; // ms
  var TRANSITION_END_BUFFER = 100; // ms thêm trước khi remove will-change

  // ============================================================
  // HELPERS
  // ============================================================

  /**
   * Lấy threshold từ data-attribute hoặc default
   */
  function getThreshold(el) {
    var t = el.getAttribute('data-reveal-threshold');
    return t ? Math.max(0, Math.min(1, parseFloat(t))) : DEFAULT_THRESHOLD;
  }

  /**
   * Tìm các child cần stagger:
   * - Ưu tiên các phần tử có [data-reveal-child]
   * - Nếu không có thì lấy toàn bộ direct children
   */
  function getStaggerTargets(parent) {
    var explicit = parent.querySelectorAll(':scope > [data-reveal-child]');
    if (explicit.length > 0) return explicit;
    return parent.children;
  }

  /**
   * Apply inline styles cho 1 element trước khi reveal
   */
  function applyElementStyles(el) {
    var delay = el.getAttribute('data-reveal-delay');
    var duration = el.getAttribute('data-reveal-duration');

    if (delay) {
      el.style.setProperty('--anweb-reveal-delay', parseInt(delay, 10) + 'ms');
      el.style.transitionDelay = parseInt(delay, 10) + 'ms';
    }
    if (duration) {
      el.style.transitionDuration = parseInt(duration, 10) + 'ms';
    }
  }

  /**
   * Set staggered delay cho từng child (override CSS nth-child)
   */
  function applyStaggerDelays(parent) {
    var gap = parseInt(parent.getAttribute('data-reveal-stagger-delay'), 10) || DEFAULT_STAGGER_GAP;
    var baseDelay = parseInt(parent.getAttribute('data-reveal-delay'), 10) || 0;
    var targets = getStaggerTargets(parent);

    Array.prototype.forEach.call(targets, function (child, i) {
      child.style.transitionDelay = baseDelay + gap * i + 'ms';
    });
  }

  /**
   * Cleanup will-change sau khi animation xong để giải phóng GPU
   */
  function scheduleCleanup(el, duration) {
    var total = duration + TRANSITION_END_BUFFER;
    setTimeout(function () {
      el.classList.add('is-revealed-done');
    }, total);
  }

  /**
   * Trigger reveal cho 1 element
   */
  function reveal(el) {
    var isStagger = el.hasAttribute('data-reveal-stagger');

    if (isStagger) {
      applyStaggerDelays(el);
      el.classList.add('is-revealed');
      // Mark children as revealed-done (cleanup will-change)
      var targets = getStaggerTargets(el);
      var gap = parseInt(el.getAttribute('data-reveal-stagger-delay'), 10) || DEFAULT_STAGGER_GAP;
      var duration = parseInt(el.getAttribute('data-reveal-duration'), 10) || 800;
      setTimeout(function () {
        Array.prototype.forEach.call(targets, function (child) {
          child.classList.add('is-revealed-done');
        });
      }, gap * targets.length + duration + TRANSITION_END_BUFFER);
    } else {
      applyElementStyles(el);
      el.classList.add('is-revealed');
      var dur = parseInt(el.getAttribute('data-reveal-duration'), 10) || 800;
      var del = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
      scheduleCleanup(el, dur + del);
    }
  }

  // ============================================================
  // OBSERVERS — group theo threshold để IO hoạt động hiệu quả
  // ============================================================
  var observerCache = {};

  function getObserver(threshold) {
    var key = threshold.toFixed(2);
    if (observerCache[key]) return observerCache[key];

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          var once = el.getAttribute('data-reveal-once') !== 'false';

          if (entry.isIntersecting) {
            reveal(el);
            if (once) io.unobserve(el);
          } else if (!once) {
            // Reset state để trigger lại lần sau
            el.classList.remove('is-revealed', 'is-revealed-done');
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: DEFAULT_ROOT_MARGIN,
      }
    );

    observerCache[key] = io;
    return io;
  }

  // ============================================================
  // INIT — observe tất cả elements hiện có
  // ============================================================
  function observeElement(el) {
    // Tránh observe trùng
    if (el.__anwebRevealObserved) return;
    el.__anwebRevealObserved = true;

    // Nếu element đã visible ngay từ đầu (above the fold) → reveal luôn,
    // không qua IO để tránh delay 1 frame
    var rect = el.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < viewportHeight * 0.9 && rect.bottom > 0) {
      // Element đã trong viewport — reveal ngay sau next frame để CSS kịp parse
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          reveal(el);
        });
      });
      return;
    }

    var observer = getObserver(getThreshold(el));
    observer.observe(el);
  }

  function scanAndObserve(root) {
    root = root || document;
    var selector = '[data-reveal], [data-reveal-stagger]';
    var elements = root.querySelectorAll(selector);
    Array.prototype.forEach.call(elements, observeElement);
  }

  // ============================================================
  // MUTATION OBSERVER — bắt elements thêm vào DOM
  // (filter/sort/pagination/quick-view modal/...)
  // ============================================================
  function initMutationObserver() {
    if (!('MutationObserver' in window)) return;

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return; // chỉ element nodes

          // Element mới có data-reveal?
          if (node.hasAttribute && (node.hasAttribute('data-reveal') || node.hasAttribute('data-reveal-stagger'))) {
            observeElement(node);
          }

          // Hoặc trong descendants?
          if (node.querySelectorAll) {
            scanAndObserve(node);
          }
        });
      });
    });

    mo.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // ============================================================
  // SHOPIFY EVENTS — re-scan khi section reload trong editor
  // hoặc khi cart drawer / quick-view mở
  // ============================================================
  function bindShopifyEvents() {
    // Custom event để dev manually re-scan
    document.addEventListener('anweb:reveal:refresh', function () {
      scanAndObserve();
    });

    // Shopify section load (theme editor + AJAX)
    document.addEventListener('shopify:section:load', function (e) {
      scanAndObserve(e.target);
    });
  }

  // ============================================================
  // BOOTSTRAP
  // ============================================================
  function init() {
    scanAndObserve();
    initMutationObserver();
    bindShopifyEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose mini API cho dev (debug / manual trigger)
  window.AnwebReveal = {
    refresh: function (root) { scanAndObserve(root); },
    reveal: reveal,
    observe: observeElement,
  };
})();
